import * as React from 'react';
import { FormField } from './form-field.js';
import { Input } from './input.js';
import { Select, type SelectOption } from './select.js';

/** Common IANA timezones in Mexico (single source for every timezone picker). */
export const MEXICO_TIMEZONES = [
  { value: 'America/Mexico_City', label: 'Centro (Ciudad de México)' },
  { value: 'America/Monterrey', label: 'Centro (Monterrey)' },
  { value: 'America/Merida', label: 'Centro (Mérida)' },
  { value: 'America/Bahia_Banderas', label: 'Centro (Bahía de Banderas)' },
  { value: 'America/Cancun', label: 'Sureste (Cancún)' },
  { value: 'America/Chihuahua', label: 'Chihuahua' },
  { value: 'America/Mazatlan', label: 'Pacífico (Mazatlán)' },
  { value: 'America/Hermosillo', label: 'Sonora (Hermosillo)' },
  { value: 'America/Tijuana', label: 'Noroeste (Tijuana)' },
  { value: 'America/Matamoros', label: 'Frontera noreste (Matamoros)' },
  { value: 'America/Ojinaga', label: 'Frontera (Ojinaga)' },
  { value: 'America/Ciudad_Juarez', label: 'Frontera (Ciudad Juárez)' },
] as const;

/** Select options for MEXICO_TIMEZONES (IANA id as description). */
export const MEXICO_TIMEZONE_OPTIONS: readonly SelectOption[] =
  MEXICO_TIMEZONES.map((z) => ({
    value: z.value,
    label: z.label,
    description: z.value,
  }));

/** True when the browser accepts `tz` as an IANA timezone. */
export function isValidTimezone(tz: string): boolean {
  if (!tz.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz.trim() });
    return true;
  } catch {
    return false;
  }
}

const OTHER = '__other__';
const INHERIT = '__inherit__';

export interface TimezoneFieldProps {
  /** IANA id; '' means "not set" (inherit when `inheritLabel` is given). */
  value: string;
  onChange: (tz: string) => void;
  error?: string;
  disabled?: boolean;
  label?: string;
  description?: React.ReactNode;
  /** Adds a first option that clears the value (e.g. "Usar la del espacio (America/Mexico_City)"). Makes the field optional. */
  inheritLabel?: string;
}

/** Timezone select of common Mexican zones plus a validated free-text IANA option. */
export function TimezoneField({
  value,
  onChange,
  error,
  disabled,
  label = 'Zona horaria',
  description,
  inheritLabel,
}: TimezoneFieldProps) {
  const known = MEXICO_TIMEZONES.some((z) => z.value === value);
  const [other, setOther] = React.useState(!known && value !== '');
  const selected = other
    ? OTHER
    : value || (inheritLabel ? INHERIT : undefined);
  return (
    <div className="grid gap-2">
      <FormField
        label={label}
        description={description}
        error={other ? undefined : error}
        required={!inheritLabel}
      >
        <Select
          value={selected}
          disabled={disabled}
          onValueChange={(v) => {
            if (v === OTHER) {
              setOther(true);
            } else {
              setOther(false);
              onChange(v === INHERIT ? '' : v);
            }
          }}
          options={[
            ...(inheritLabel ? [{ value: INHERIT, label: inheritLabel }] : []),
            ...MEXICO_TIMEZONE_OPTIONS,
            { value: OTHER, label: 'Otra zona (IANA)' },
          ]}
        />
      </FormField>
      {other ? (
        <FormField
          label="Zona horaria IANA"
          description="Por ejemplo America/Bogota. Se valida con el navegador."
          error={error}
          required
        >
          <Input
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            placeholder="America/Bogota"
          />
        </FormField>
      ) : null}
    </div>
  );
}
